import { APP_ORIGINS, AppSurface, absoluteUrl, isKnownOrigin } from '@core/config/app-surface';
import { UserModel } from '@core/store/user/user.model';
import { MenuItem, MenuSection } from '@v2/shell/menu.model';

/**
 * One destination in the rail or the tab bar.
 *
 * `route` and `href` are mutually exclusive: `route` is an in-app path handed
 * to `routerLink`, `href` is anything that leaves the app -- another EVO
 * product, a phone number, a mailbox. Exactly one of them is set, and which
 * one it is decides how the link is rendered.
 *
 * `icon` is a key of `V2_ICONS` rather than an imported symbol, so this file
 * stays a plain data module with no dependency on the icon package.
 */
export interface V2NavItem {
  label: string;
  route?: string;
  href?: string;
  icon: string;
  /**
   * Which host serves `route`. Absent means the estimator, which serves
   * everything except the two briefing pages.
   */
  surface?: AppSurface;
}

export interface V2NavGroup {
  title: string;
  items: V2NavItem[];
}

/**
 * The rail's contents, transcribed from `core.component.html`.
 *
 * The visibility rules are deliberately identical to the old sidebar's: the
 * three admin groups behind is_staff, the two 24/7 desks behind NOT is_staff,
 * EVO Apps unconditional. This is a re-skin -- changing who sees what is a
 * product decision, and making one quietly inside a restyling job is how such
 * rules get changed by accident.
 *
 * Every customer with access to this platform is entitled to the 24/7 desk
 * numbers, so showing them to all non-admins is intended, not a leak.
 *
 * A null user is treated as not-an-admin: the store starts empty and the rail
 * renders before the user request settles, so the safe first paint is the one
 * that shows nothing privileged.
 */
export function v2NavGroups(user: UserModel | null, surface: AppSurface): V2NavGroup[] {
  const groups: V2NavGroup[] = [];
  const isAdmin = !!user?.is_staff;

  if (isAdmin) {
    groups.push(
      {
        title: 'Briefings',
        items: [
          { label: 'Airport Management', route: '/airports', icon: 'lucidePlane' },
          { label: 'Handler Management', route: '/handlers', icon: 'lucideLayoutGrid' },
          { label: 'Feedback Management', route: '/feedback', icon: 'lucideFileText' },
        ],
      },
      {
        title: 'Price estimator',
        items: [
          // "Management" is dropped from these two, and only these two: they
          // were the labels long enough to truncate in a 232px rail, and
          // truncation on a navigation item hits the one string a reader needs
          // whole -- it is the text you read to decide where to go. The group
          // title above already says "Price estimator", so nothing is lost.
          { label: 'Airport Fees', route: '/airport-fees', icon: 'lucideFileText' },
          { label: 'Handler Fees', route: '/admin/handler-fees', icon: 'lucideFileText' },
          // No count on it, deliberately. This function is a pure statement of
          // who sees what; a live count is neither pure nor about the user, and
          // the bell carries it three inches away. See the design spec, §5.
          { label: 'Price requests', route: '/admin/price-requests', icon: 'lucideFileText' },
          { label: 'Estimator', route: '/admin/cost-estimator', icon: 'lucideLayoutGrid' },
          { label: 'EVO Tariffs', route: '/admin/tariffs', icon: 'lucideFileText' },
          { label: 'Aircraft Types', route: '/aircraft', icon: 'lucidePlane' },
          { label: 'Estimator FAQ', route: '/faq', icon: 'lucideFileText' },
        ],
      },
      {
        title: 'Settings',
        items: [{ label: 'Accounts', route: '/accounts', icon: 'lucideSettings' }],
      },
    );
  }

  groups.push({
    title: 'EVO Apps',
    items: [
      { label: 'Fuel Web', href: 'https://apps.evo-jet.com/fuel/search/airports', icon: 'lucideFuel' },
      { label: 'Flight Compass', href: 'https://www.flight-compass.com/trips/', icon: 'lucideExternalLink' },
      { label: 'Airport Fee Estimator', route: '/', icon: 'lucideLayoutGrid' },
      { label: 'Airport Briefings', route: '/', surface: 'briefings', icon: 'lucidePlane' },
      { label: '3D Airport Briefing', href: 'https://flyevo.com/airport-briefing-app/', icon: 'lucideExternalLink' },
      { label: 'NOTAMizer', href: 'https://notams.flyevo.com/', icon: 'lucideExternalLink' },
    ],
  });

  if (!isAdmin) {
    groups.push(
      {
        title: '24/7 Ops Center',
        items: [
          { label: '+1 415 404 6104', href: 'tel:+14154046104', icon: 'lucideLifeBuoy' },
          { label: 'opscenter@flyevo.com', href: 'mailto:opscenter@flyevo.com', icon: 'lucideLifeBuoy' },
        ],
      },
      {
        title: '24/7 Fuel Dispatch',
        items: [
          { label: '+1 415 404 6133', href: 'tel:+14154046133', icon: 'lucideFuel' },
          { label: 'orderfuel@flyevo.com', href: 'mailto:orderfuel@flyevo.com', icon: 'lucideFuel' },
        ],
      },
    );
  }

  return groups.map(group => ({
    ...group,
    items: group.items.map(item => toCurrentSurface(item, surface)),
  }));
}

/**
 * A nav row as this host can render it.
 *
 * A `route` that belongs to the other surface cannot be a `routerLink` -- the
 * router on this host has no such route, and would land on the stray-path
 * redirect. It becomes an absolute `href` on the owning origin instead, which
 * also makes it render with the external-link affordance every other
 * off-this-app destination gets.
 */
function toCurrentSurface(item: V2NavItem, surface: AppSurface): V2NavItem {
  if (item.href || !item.route) {
    return item;
  }
  const owner: AppSurface = item.surface ?? 'estimator';
  if (owner === surface) {
    return item;
  }
  const { route, surface: _owner, ...rest } = item;
  return { ...rest, href: absoluteUrl(owner, route, APP_ORIGINS) };
}

/** A stable id for a group or a row, derived from the text it shows. */
function slug(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

/**
 * The same groups, in the shape the ported rail speaks.
 *
 * `v2NavGroups` stays the single statement of who sees what -- this is a pure
 * rename of its output into evo-accounts' `MenuSection`/`MenuItem` types, which
 * is what its rail and its menu-item component are written against. Nothing is
 * added, removed or reordered here, so the specs that pin the visibility rules
 * pin this too.
 *
 * Three fields are derived rather than carried:
 *
 *  - `id`, slugged from the label, because the rail persists which sections are
 *    folded away and needs a key that is stable across renders. Titles are
 *    unique within the nav and labels are unique within a group.
 *  - `isExternal`, true only for a web destination. A `tel:` or `mailto:` href
 *    handed to a new tab opens a blank one on a desktop browser, and an arrow
 *    beside a phone number promises a page that is never going to load.
 *  - `mono`, for a phone number -- a value read digit by digit, which the data
 *    face with tabular figures makes scannable.
 */
export function v2MenuSections(user: UserModel | null, surface: AppSurface): MenuSection[] {
  return v2NavGroups(user, surface).map(group => {
    const id = slug(group.title);
    return {
      id,
      label: group.title,
      collapsible: true,
      items: group.items.map((item): MenuItem => {
        const href = item.href;
        return {
          id: `${id}--${slug(item.label)}`,
          label: item.label,
          icon: item.icon,
          route: item.route,
          href,
          isExternal: !!href && href.startsWith('http'),
          // The other surface of this same product is not a new tab. It is
          // external enough to earn the arrow -- the address changes -- but a
          // user who follows it is going somewhere they will want to come back
          // from, and the mobile tab bar already navigates these in place.
          newTab: !!href && href.startsWith('http') && !isKnownOrigin(href, APP_ORIGINS),
          mono: !!href && href.startsWith('tel:'),
        };
      }),
    };
  });
}

/**
 * The phone's bottom navigation. Four destinations, always -- a bar that
 * changes width between viewers is a bar people have to re-learn.
 *
 * The fourth slot is Support for a client and Admin for an admin, because
 * admins do not get the 24/7 groups and clients have nothing to administer.
 *
 * New: the app has no mobile navigation today.
 */
export function v2TabBarItems(user: UserModel | null, surface: AppSurface): V2NavItem[] {
  const isAdmin = !!user?.is_staff;

  const items: V2NavItem[] = [
    { label: 'Estimator', route: '/', icon: 'lucideLayoutGrid' },
    { label: 'Briefings', route: '/', surface: 'briefings', icon: 'lucidePlane' },
    { label: 'Fuel', href: 'https://apps.evo-jet.com/fuel/search/airports', icon: 'lucideFuel' },
    isAdmin
      ? { label: 'Admin', route: '/airports', icon: 'lucideSettings' }
      : { label: 'Support', href: 'tel:+14154046104', icon: 'lucideLifeBuoy' },
  ];

  return items.map(item => toCurrentSurface(item, surface));
}
